import React, { useEffect, useState } from "react";
import { EditDialog } from "../../external/ensolvers-core-frontend-mui/components/EditDialog";
import { DialogPosition } from "../../external/ensolvers-core-frontend-mui/components/Common/DialogPosition";
import Assignation from "../../models/Assignation";
import AssignationService from "../../services/AssignationService";
import NotificationService from "../../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import RoleUtils from "../../utils/RoleUtils";
import { SpinnerWrapper } from "../SpinnerWrapper";
import AssignationEditionFields from "./Fields/AssignationEditionFields";

interface Props {
  externalId: string;
  onClose: (updated: boolean) => void;
}

function AssignationEditDialog({ externalId, onClose }: Props) {
  const [isLoading, setIsLoading] = useState(false);
  const [instance, setInstance] = useState<Assignation | undefined>();
  const [assignationStartDate, setAssignationStartDate] = useState<string>("");
  const [assignationEndDate, setAssignationEndDate] = useState<string | undefined>();
  const [showDialog, setShowDialog] = useState(true);
  const [canSave, setCanSave] = useState(true);

  useEffect(() => {
    fetchAssignation(externalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAssignation = async (externalId: string) => {
    setIsLoading(true);
    try {
      const assignation = (await AssignationService.getByExternalId(externalId)).getContent();
      setInstance(assignation);
      setAssignationStartDate(new Date(assignation.shortStartDate).getTime().toString());
    } catch (error) {
      NotificationService.error("Error getting assignation");
    } finally {
      setIsLoading(false);
    }
  };

  const validateDates = () => {
    if (!instance) return;

    try {
      AssignationService.validateAssignationStartDate(instance.position.externalId!, assignationStartDate, assignationEndDate || "");
      setCanSave(true);
    } catch (error: any) {
      NotificationService.error(error.response.data.message);
      setCanSave(false);
    }
  };

  const handleUpdateStartDate = (startDate: number) => {
    setAssignationStartDate(startDate.toString());
    validateDates();
  };

  const handleUpdateEndDate = (endDate?: number) => {
    setAssignationEndDate(endDate?.toString());
    validateDates();
  };

  const handleUpdateAssignation = async (assignation: Assignation) => {
    setShowDialog(false);
    if (!RoleUtils.isAdminOrManager()) {
      NotificationService.error("You don't have permission to do this action");
      return;
    }

    try {
      await AssignationService.update(assignation);
      onClose(true);
      NotificationService.info("Updated successfully");
    } catch (error: any) {
      NotificationService.error(error.response.data.message);
      onClose(false);
    }
  };

  const handleOnCancel = () => {
    setShowDialog(false);
    onClose(false);
  };

  return (
    <SpinnerWrapper isLoading={isLoading}>
      <EditDialog<Assignation>
        elementName={"Assignation"}
        dialogPosition={DialogPosition.Center}
        currentInstance={instance}
        newItemDialogOpen={showDialog}
        disableSaveButton={!canSave}
        onUpdate={(assignation) => {
          handleUpdateAssignation(assignation);
        }}
        onCancel={handleOnCancel}
        renderDialogContent={(register, errors, control) => (
          <AssignationEditionFields
          control={control}
          register={register}
          errors={errors}
          onStartDateChange={handleUpdateStartDate}
          onEndDateChange={handleUpdateEndDate}
          />
          )}
          buttonStyles={{}}
          setLastUpdateTimestamp={() => {}}
          setNewInstanceDialogOpen={() => {}}
      />
    </SpinnerWrapper>
  );
}

export default AssignationEditDialog;
